import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from '../hooks/useSocket'; 
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead as apiMarkNotificationAsRead,
  markAllAsRead as apiMarkAllAsRead,
  deleteNotification as apiDeleteNotification,
  deleteAllNotifications as apiDeleteAllNotifications
} from '../api/notifications';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket(); 
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transientNotification, setTransientNotification] = useState(null);

  const persistNotifications = useCallback((entries) => {
    if (user?.id && typeof window !== 'undefined') {
      window.localStorage.setItem(`notifications_${user.id}`, JSON.stringify(entries));
    }
  }, [user?.id]);

  const loadNotifications = useCallback(async () => {
    const activeId = user?.id || user?.profile?.sub;
    
    if (!activeId) return;

    setLoading(true);
    try {
      const [listResponse, unreadResponse] = await Promise.all([
        getNotifications(),
        getUnreadCount()
      ]);

      const rawEntries = listResponse?.notifications || [];
      const entries = rawEntries.map(notif => ({
        id: notif._id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        username: notif.relatedUser?.username || notif.metadata?.firstLiker || 'Someone',
        avatar: notif.relatedUser?.profile?.avatar || null,
        thumbnail: notif.relatedVideo?.thumbnail || null,
        read: notif.isRead,
        timestamp: notif.createdAt,
        metadata: notif.metadata || {}
      }));

      setNotifications(entries);
      setUnreadCount(unreadResponse?.unreadCount || 0);
      
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(`notifications_${activeId}`, JSON.stringify(entries));
      }
    } catch (error) {
      console.error('[Notifications] API Error:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [loadNotifications, user]);

  const handleRealTimeNotification = useCallback((newNotif) => {
    const mappedNotif = {
      id: newNotif._id,
      type: newNotif.type,
      title: newNotif.title,
      message: newNotif.message,
      username: newNotif.relatedUser?.username || newNotif.metadata?.firstLiker || 'Someone',
      avatar: newNotif.relatedUser?.profile?.avatar || null,
      thumbnail: newNotif.relatedVideo?.thumbnail || null,
      read: newNotif.isRead || false,
      timestamp: newNotif.createdAt, 
      metadata: newNotif.metadata || {}
    };

    setTransientNotification(mappedNotif);
    setTimeout(() => {
      setTransientNotification(null);
    }, 1500);

    setNotifications(prev => {
      const existingIndex = prev.findIndex(n => n.id === mappedNotif.id);
      
      if (existingIndex !== -1) {
        const updatedList = [...prev];
        const oldNotif = updatedList[existingIndex];
        
        updatedList.splice(existingIndex, 1);
        updatedList.unshift(mappedNotif);

        if (oldNotif.read && !mappedNotif.read) {
          setUnreadCount(c => c + 1);
        }
        
        return updatedList;
      } else {
        if (!mappedNotif.read) {
          setUnreadCount(c => c + 1);
        }
        return [mappedNotif, ...prev];
      }
    });

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(mappedNotif.title, { body: mappedNotif.message });
      try { new Audio('/notification.mp3').play().catch(() => {}); } catch(e) {}
    }
  }, []);

  useEffect(() => {
    if (socket && isConnected && user) {
      socket.emit('subscribe:notifications');
      socket.on('notification', handleRealTimeNotification);
    }

    return () => {
      if (socket) {
        socket.off('notification', handleRealTimeNotification);
      }
    };
  }, [socket, isConnected, user, handleRealTimeNotification]);

  const markAsRead = useCallback(async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await apiMarkNotificationAsRead(id);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await apiMarkAllAsRead();
    } catch (error) {
      console.error(error);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    transientNotification,
    markAsRead,
    markAllAsRead,
    refreshNotifications: loadNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};